<template>
  <div class="bg-secondary-dark h-full">
    <div>
      <div class="flex justify-end pr-2 pt-2">
        <button
          type="button"
          class="text-secondary-lighter hover:text-common-white hover:bg-secondary rounded-sm p-1 cursor-pointer transition-colors"
          :aria-label="sidebarCollapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'"
          @click="handleSidebarToggleClick"
        >
          <BIconChevronDoubleRight v-if="sidebarCollapsed" class="w-5 h-5" />
          <BIconChevronDoubleLeft v-else class="w-5 h-5" />
        </button>
      </div>

      <div class="flex items-center px-4 pt-3 pb-6 w-auto overflow-hidden">
        <div class="flex gap-4 overflow-hidden">
          <NuxtLink :to="`/client/${currentClient?.client_id_pk}/dashboard`">
            <div
              :class="[
                enrichedClient ? enrichedClient.bgColor : 'bg-red-light',
                sidebarCollapsed ? 'w-8 h-8' : 'w-10 h-10',
                'shrink-0 flex items-center justify-center text-common-white text-md rounded-md font-bold',
              ]"
            >
              {{ enrichedClient?.initials }}
            </div>
          </NuxtLink>
          <div class="flex flex-col">
            <p
              v-if="!sidebarCollapsed"
              class="text-common-white font-bold my-auto truncate"
              :class="{ 'cursor-pointer': isAdminUser }"
              :title="isAdminUser ? `${enrichedClient?.title} (Klicken, um die UUID zu kopieren)` : enrichedClient?.title"
              @click="copyClientUUID"
            >
              {{ enrichedClient?.title }}
            </p>
            <p
              v-if="!sidebarCollapsed"
              class="text-common-white text-sm my-auto truncate"
            >
              {{ getClientMode(currentClient?.client_mode) }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex grow flex-col">
        <nav class="flex-1 space-y-2 px-2 justify-center" aria-label="Sidebar">
          <div v-for="n in nav" :key="n.to">
            <NuxtLink
              v-if="!n.submenus || n.submenus.length === 0"
              :to="n.to"
              class="group flex justify-between py-2 px-2 text-md"
            >
              <component
                :is="n.icon"
                :class="[
                  n.isActive
                    ? 'text-lilac'
                    : 'text-secondary-lighter group-hover:text-common-white',
                  sidebarCollapsed ? 'my-auto mx-auto' : 'mr-3',
                  'w-5 h-5 self-center',
                ]"
              />

              <span
                v-if="!sidebarCollapsed"
                :class="[
                  n.isActive
                    ? 'text-lilac font-bold'
                    : 'text-secondary-lighter group-hover:text-common-white',
                  'flex-1 ',
                ]"
              >{{ n.title }}</span>

              <span v-if="n.alerts > 0 && !sidebarCollapsed" class="bg-secondary-highlight-light text-common-white text-xs font-bold ml-2 px-2.5 py-1 rounded-full my-auto"> {{ n?.alerts }}</span>
            </NuxtLink>

            <div v-else>
              <div
                class="group flex justify-between py-2 px-2 text-md cursor-pointer"
                @click="handleNavClick(n, $event)"
              >
                <component
                  :is="n.icon"
                  :class="[
                    n.isActive
                      ? 'text-lilac'
                      : 'text-secondary-lighter group-hover:text-common-white',
                    sidebarCollapsed ? 'my-auto mx-auto' : 'mr-3',
                    'w-5 h-5 self-center',
                  ]"
                />

                <span
                  v-if="!sidebarCollapsed"
                  :class="[
                    n.isActive
                      ? 'text-lilac font-bold'
                      : 'text-secondary-lighter group-hover:text-common-white',
                    'flex-1 ',
                  ]"
                >{{ n.title }}</span>

                <span v-if="getSubmenuAlerts(n) > 0 && !sidebarCollapsed && closedSubMenus.has(n.to)" class="bg-secondary-highlight-light text-common-white text-xs font-bold ml-2 px-2.5 py-1 rounded-full my-auto"> {{ getSubmenuAlerts(n) }}</span>

                <component
                  :is="!closedSubMenus.has(n.to) ? BIconChevronDown : BIconChevronUp"
                  v-if="!sidebarCollapsed"
                  :class="[
                    n.isActive
                      ? 'text-lilac'
                      : 'text-secondary-lighter group-hover:text-common-white',
                    'w-4 h-4 self-center ml-2',
                  ]"
                  @click.stop="toggleSubmenu(n)"
                />
              </div>

              <div v-if="!closedSubMenus.has(n.to) && !sidebarCollapsed" class="ml-8">
                <NuxtLink
                  v-for="submenu in n.submenus"
                  :key="submenu.to"
                  :to="submenu.to"
                  class="group flex justify-between py-1 px-2 text-sm"
                >
                  <span
                    :class="[
                      route.path === submenu.to
                        ? 'text-lilac font-bold'
                        : 'text-secondary-lighter group-hover:text-common-white',
                    ]"
                  >{{ submenu.title }}</span>
                  <span v-if="submenu.alerts > 0" class="bg-secondary-highlight-light text-common-white text-xs font-bold ml-2 px-2 py-0.5 rounded-full">{{ submenu.alerts }}</span>
                </NuxtLink>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  BIconGrid,
  BIconGear,
  BIconClockHistory,
  BIconChevronDoubleLeft,
  BIconChevronDoubleRight,
  BIconSearch,
  BIconCrosshair,
  BIconJournalText,
  BIconExclamationOctagon,
  BIconChevronDown,
  BIconChevronUp
} from 'bootstrap-icons-vue'

import { createEnrichedProjectCard } from '~/utils/other'

import { useCurrentClient } from '~/composables/useCurrentClient'
const route = useRoute()
const { currentClient } = useCurrentClient()
const { $gql, $logger, $event, $toast } = useNuxtApp()
const { isAdminUser } = useCurrentUserSession()

const props = defineProps({
  clientMode: {
    type: String,
    required: true
  }
})

const alerts = ref({
  dashboard: 0,
  changeHistory: 0,
  ioe: 0,
  ioa: 0,
  settings: 0,
  securityScore: 0
})

async function copyClientUUID () {
  if (!isAdminUser || !currentClient?.value?.client_id_pk) {
    return
  }
  try {
    await navigator.clipboard.writeText(currentClient.value.client_id_pk)
    $toast.success('Client-UUID kopiert')
  } catch (e) {
    $toast.error('Fehler beim Kopieren', e?.message)
    $logger.error(e)
  }
}

// Start expanded on every screen size so the submenus stay reachable; collapsing
// is a manual choice via the toggle above.
const sidebarCollapsed = ref(false)

const closedSubMenus = ref(new Set())
const isSubmenusInitialized = ref(false)

const nav = computed(() => {
  const baseNav = [
    {
      title: 'Dashboard',
      to: `/client/${currentClient?.value?.client_id_pk}/dashboard`,
      icon: BIconGrid,
      isActive: route.path.startsWith(`/client/${currentClient?.value?.client_id_pk}/dashboard`),
      alerts: alerts.value.dashboard,
      submenus: []
    },
    {
      title: 'Explorer',
      to: `/client/${currentClient?.value?.client_id_pk}/explorer`,
      icon: BIconSearch,
      isActive: route.path.startsWith(`/client/${currentClient?.value?.client_id_pk}/explorer`),
      alerts: alerts.value.changeHistory,
      submenus: [
        { title: 'Übersicht', to: `/client/${currentClient?.value?.client_id_pk}/explorer` },
        { title: 'Privilegierte Accounts', to: `/client/${currentClient?.value?.client_id_pk}/explorer/privileges` }
      ]
    },
    {
      title: 'Änderungsverlauf',
      to: `/client/${currentClient?.value?.client_id_pk}/change-history`,
      icon: BIconClockHistory,
      isActive: route.path.startsWith(`/client/${currentClient?.value?.client_id_pk}/change-history`),
      alerts: alerts.value.changeHistory,
      submenus: [
        { title: 'Suche', to: `/client/${currentClient?.value?.client_id_pk}/change-history/search` }
      ]
    },
    {
      title: 'Indicators of Exposure',
      to: `/client/${currentClient?.value?.client_id_pk}/ioe`,
      icon: BIconExclamationOctagon,
      isActive: route.path.startsWith(`/client/${currentClient?.value?.client_id_pk}/ioe`),
      alerts: alerts.value.ioe,
      submenus: []
    },
    {
      title: 'Einstellungen',
      to: `/client/${currentClient?.value?.client_id_pk}/settings`,
      icon: BIconGear,
      isActive: route.path.startsWith(`/client/${currentClient?.value?.client_id_pk}/settings`),
      submenus: [
        { title: 'Allgemein', to: `/client/${currentClient?.value?.client_id_pk}/settings`, alerts: alerts.value.settings },
        { title: 'Indicators of Exposure', to: `/client/${currentClient?.value?.client_id_pk}/settings/ioe` }
      ]
    }
  ]

  // extend nav for monitoring mode
  if (props.clientMode === 'monitoring') {
    // add live submenu to change history
    baseNav[2].submenus.unshift({ title: 'Live', to: `/client/${currentClient?.value?.client_id_pk}/change-history` })

    // add ioa and security log nav items
    baseNav.splice(4, 0, {
      title: 'Indicators of Attack',
      to: `/client/${currentClient?.value?.client_id_pk}/ioa`,
      icon: BIconCrosshair,
      isActive: route.path.startsWith(`/client/${currentClient?.value?.client_id_pk}/ioa`),
      alerts: alerts.value.ioa,
      submenus: []
    })
    baseNav.splice(5, 0, {
      title: 'Sicherheitslog',
      to: `/client/${currentClient?.value?.client_id_pk}/security-log`,
      icon: BIconJournalText,
      isActive: route.path.startsWith(`/client/${currentClient?.value?.client_id_pk}/security-log`),
      alerts: alerts.value.securityScore,
      submenus: []
    })

    // add new settings submenu
    baseNav[baseNav.length - 1].submenus.push({ title: 'Indicators of Attack', to: `/client/${currentClient?.value?.client_id_pk}/settings/ioa` })
    baseNav[baseNav.length - 1].submenus.push({ title: 'Integrationen', to: `/client/${currentClient?.value?.client_id_pk}/settings/integrations` })
    baseNav[baseNav.length - 1].submenus.push({ title: 'Reporting', to: `/client/${currentClient?.value?.client_id_pk}/settings/reporting` })
    baseNav[baseNav.length - 1].submenus.push({ title: 'MCP', to: `/client/${currentClient?.value?.client_id_pk}/settings/mcp` })
  }

  return baseNav
})

const enrichedClient = computed(() => {
  if (currentClient?.value?.client_name) {
    const client = createEnrichedProjectCard([currentClient.value])[0]
    client.bgColor = 'bg-red-light'
    return client
  }
})

const alertQuery = computed(() => `query Alerts ($clientUUID: uuid!) {
  tasks: tbl_notifications_aggregate(where: {  _and: [ {notification_deleted: {_eq: false}}, {notification_is_read: {_eq: false}}, {notification_type: {_eq: "task" }}, {client_uuid: {_eq: $clientUUID}} ]}) {
    aggregate {
      count
    }
  }
 }`)

useAsyncData(() => fetchAlerts())

function getClientMode (mode) {
  if (mode === 'audit') {
    return 'Audit'
  } else if (mode === 'quickcheck') {
    return 'QuickCheck'
  } else if (mode === 'monitoring') {
    return 'Monitoring'
  }
}

async function fetchAlerts () {
  try {
    const { data } = await $gql.query(alertQuery.value, {
      clientUUID: currentClient?.value?.client_id_pk
    })

    if (data.tasks) {
      alerts.value.settings = data.tasks.aggregate.count
    }
  } catch (e) {
    $logger.error(e)
  }
}

function handleSidebarToggleClick () {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

function handleNavClick (navItem, event) {
  if (navItem.submenus && navItem.submenus.length > 0) {
    event.preventDefault()

    // Always navigate to first submenu
    navigateTo(navItem.submenus[0].to)

    // Toggle submenu visibility
    if (closedSubMenus.value.has(navItem.to)) {
      closedSubMenus.value.delete(navItem.to)
    } else {
      closedSubMenus.value.add(navItem.to)
    }
  }
}

function toggleSubmenu (navItem) {
  // Only toggle submenu visibility, don't navigate
  if (closedSubMenus.value.has(navItem.to)) {
    closedSubMenus.value.delete(navItem.to)
  } else {
    closedSubMenus.value.add(navItem.to)
  }
}

function getSubmenuAlerts (navItem) {
  if (!navItem.submenus || navItem.submenus.length === 0) {
    return 0
  }
  return navItem.submenus.reduce((sum, submenu) => sum + (submenu.alerts || 0), 0)
}

// Initialize settings submenu as closed by default
watch(nav, (newNav) => {
  if (!isSubmenusInitialized.value && newNav.length > 0) {
    // Add only the settings menu to closedSubMenus
    newNav.forEach((navItem) => {
      if (navItem.submenus && navItem.submenus.length > 0 && navItem.to.includes('/settings')) {
        closedSubMenus.value.add(navItem.to)
      }
    })
    isSubmenusInitialized.value = true
  }
}, { immediate: true })

// emitted in NotificationList.vue
$event.listen('task:add', () => alerts.value.settings++)
$event.listen('task:remove', () => alerts.value.settings--)

</script>

<style scoped></style>
